<template>
  <div class="wrapper">
    <b-loading
      :is-full-page="true"
      :active.sync="isLoading"
      :can-cancel="false"
    ></b-loading>
    <b-modal
      v-if="isCameraDisplayed"
      :active="true"
      class="dij-diagrambuilder-camera-modal"
      full-screen
    >
      <camera @cancel="hideCamera" @ok="processCamera" />
    </b-modal>
    <div class="items-container">
      <div
        class="instruction full"
        :class="{ 'child-question': instruction.parentId }"
        v-for="(instruction, index) in taskQuestions"
      >
        <div class="instruction-title">
          {{ instruction.label }}.<span v-if="instruction.subLabel"
            >{{ instruction.subLabel }}
          </span>
          <span>{{ instruction.question }}</span>
          <div class="right-side-title">
            <div
              v-if="instruction.isPictureMandatory"
              class="req-picture"
              :style="iconRequirePictureStyle(instruction)"
            >
              {{ $t('picture_required') }}
            </div>
            <template v-if="!isTablet">
              <b-dropdown
                :disabled="isTaskCompleted"
                :expanded="true"
                position="is-bottom-left"
                :close-on-click="false"
                :ref="'fileUploadDropdown' + index"
              >
                <template #trigger>
                  <b-icon
                    icon="camera"
                    size="is-large"
                    :class="[
                      'instruction-action',
                      'camera',
                      { 'is-disabled': isTaskCompleted },
                      {
                        'animate-slide':
                          instruction.isPictureMandatory &&
                          instruction.image &&
                          instruction.image.length === 0,
                      },
                    ]"
                    :style="iconRequirePictureStyle(instruction)"
                  />
                </template>
                <b-dropdown-item>
                  <b-upload
                    :disabled="isTaskCompleted"
                    multiple
                    accept=".jpg, .jpeg, .png, .pdf"
                    class="file-label"
                    @input="
                      (event) => onUploadImageWrapper(event, instruction, index)
                    "
                    v-model="imagesToUpload"
                  >
                    <span class="file-upload icon instruction-action is-large"
                      ><i class="mdi mdi-folder mdi-48px"> </i
                    ></span>
                    <span>{{ $t('instruction_view.choose_file') }}</span>
                  </b-upload>
                </b-dropdown-item>
                <b-dropdown-item @click.native="showCamera(instruction)">
                  <b-icon
                    icon="camera"
                    size="is-large"
                    class="instruction-action camera"
                  />
                  <span>{{ $t('instruction_view.take_photo') }}</span>
                </b-dropdown-item>
              </b-dropdown>
            </template>

            <template v-if="isTablet">
              <b-upload
                :disabled="isTaskCompleted"
                multiple
                accept=".jpg, .jpeg, .png, .pdf"
                class="file-label"
                @input="(event) => onUploadImage(event, instruction)"
                v-model="imagesToUpload"
              >
                <span
                  :class="[
                    'file-upload',
                    'icon',
                    'instruction-action',
                    'is-large',
                    { 'is-disabled': isTaskCompleted },
                    {
                      'animate-slide':
                        instruction.isPictureMandatory &&
                        instruction.image &&
                        instruction.image.length === 0,
                    },
                  ]"
                  :style="iconRequirePictureStyle(instruction)"
                  ><i class="mdi mdi-camera mdi-48px"></i
                ></span>
              </b-upload>
              <!-- <div class="dij-diagrambuilder-camera">
                <b-icon
                  icon="camera"
                  size="is-large"
                  class="instruction-action camera"
                  :style="iconRequirePictureStyle(instruction)"
                  :class="{
                    'animate-slide':
                      instruction.isPictureMandatory &&
                      instruction.image &&
                      instruction.image.length === 0,
                  }"
                  @click.native="showCamera(instruction)"
                />
              </div> -->
            </template>

            <div class="icon-container">
              <b-icon
                icon="image"
                size="is-large"
                :class="[
                  'instruction-action',
                  { 'is-disabled': isTaskCompleted },
                ]"
                @click.native="showGallery(instruction)"
                :style="iconRequirePictureStyle(instruction)"
              ></b-icon>
              <span
                v-if="instruction.image && instruction.image.length > 0"
                class="image-counter"
              >
                {{ instruction.image.length }}
              </span>
            </div>
            <b-button
              v-if="['tablequestion', 'importcsv'].includes(instruction.type)"
              type="is-success"
              :loading="!!savingStates[instruction._id]"
              :disabled="isTaskCompleted || savingStates[instruction._id]"
              @click="onSaveTable(instruction)"
            >
              {{ $t('admin_page.save') }}
            </b-button>
          </div>
        </div>
        <div class="image-container-helper" v-if="instruction.imageReference">
          <img
            :src="getImageReference(instruction)"
            :alt="$t('instruction_view.image_alt')"
          />
        </div>
        <div class="instruction-details">
          <div class="question-details" v-if="instruction.type === 'action'">
            <b-checkbox
              :disabled="isTaskCompleted"
              v-model="instruction.checked"
              type="is-success"
              size="is-medium"
              @input="debouncedUpdateQuestion(instruction)"
            >
              {{ $t('instruction_view.done') }}
            </b-checkbox>
          </div>
          <div
            class="question-details question-details-yes-no"
            v-if="isYesNoType(instruction)"
          >
            <b-radio-button
              v-for="option in options"
              :key="option.value"
              :disabled="isTaskCompleted"
              v-model="instruction.yesOrNo"
              :native-value="option.value"
              size="is-medium"
              :type="option.type"
              @input="debouncedUpdateQuestion(instruction)"
            >
              <b-icon :icon="option.icon"></b-icon>
              <span>{{ option.label }}</span>
            </b-radio-button>
          </div>
          <div
            class="question-details"
            v-if="instruction.type === 'textnumber'"
          >
            <!-- <b-input
              expanded
              :disabled="isTaskCompleted"
              :placeholder="$t('tap_to_start_writing')"
              v-model="instruction.inputValue"
              @input="debouncedUpdateQuestion(instruction)"
            ></b-input> -->
            <RichTextEditor
              :disabled="isTaskCompleted"
              v-model="instruction.inputValue"
              @input="debouncedUpdateQuestion(instruction)"
            />
          </div>
          <div
            v-if="instruction.type === 'multiplechoice'"
            class="question-details"
            style="display: flex; gap: 10px; align-items: flex-start"
          >
            <template
              v-for="(option, optionIndex) in instruction.multipleChoices"
            >
              <b-checkbox
                v-if="!option.other"
                :disabled="isTaskCompleted"
                size="is-medium"
                v-model="option.checked"
                type="is-success"
                @input="debouncedUpdateQuestion(instruction)"
              >
                {{ option.text?.defaultMC || option?.text || option.defaultMC }}
              </b-checkbox>

              <div class="other-option" v-if="option.other">
                <b-checkbox
                  :disabled="isTaskCompleted"
                  size="is-medium"
                  type="is-success"
                  @input="onCheckOtherOption(instruction)"
                  :value="
                    instruction.multipleChoices.find((opt) => opt.other)
                      ?.checked || false
                  "
                >
                </b-checkbox>
                <b-input
                  :readonly="!hasOtherOption(instruction)"
                  :placeholder="$t('other')"
                  @input="inputOtherOption(instruction, $event)"
                  :value="
                    instruction.multipleChoices.find((opt) => opt.other)
                      ?.defaultMC
                  "
                ></b-input>
              </div>
            </template>
          </div>
          <div
            v-if="instruction.type === 'singlechoice'"
            class="question-details"
            style="display: flex; gap: 10px; align-items: flex-start"
          >
            <template
              v-for="(option, optionIndex) in instruction.multipleChoices"
            >
              <b-radio
                v-if="!option.other"
                :disabled="isTaskCompleted"
                size="is-medium"
                :value="!option.checked"
                type="is-success"
                @input="radioOptionChanged(instruction, optionIndex)"
              >
                {{ option.text?.defaultMC || option?.text || option.defaultMC }}
              </b-radio>
              <div class="other-option" v-if="option.other">
                <b-radio
                  :disabled="isTaskCompleted"
                  size="is-medium"
                  type="is-success"
                  @input="otherSingleChoiceOption(instruction)"
                  :value="
                    !instruction.multipleChoices.find((opt) => opt.other)
                      ?.checked || false
                  "
                >
                </b-radio>
                <b-input
                  :readonly="!hasOtherOption(instruction)"
                  :placeholder="$t('other')"
                  @input="inputOtherOption(instruction, $event)"
                  :value="
                    instruction.multipleChoices.find((opt) => opt.other)
                      ?.defaultMC
                  "
                ></b-input>
              </div>
            </template>
          </div>
          <div
            v-if="['tablequestion', 'importcsv'].includes(instruction.type)"
            class="question-details"
          >
            <div
              v-if="instruction.type === 'tablequestion'"
              class="table-description"
            >
              {{ $t('table') }} {{ instruction.dimensions }}
            </div>

            <div
              v-if="['tablequestion', 'importcsv'].includes(instruction.type)"
              class="image-container"
            >
              <div v-if="getTableImage(instruction)">
                <img
                  :src="getTableImage(instruction)"
                  :alt="$t('instruction_view.upload_image_alt')"
                />
              </div>
              <div v-else>
                <b-icon icon="image-area" size="is-medium"></b-icon>
              </div>
            </div>
            <TableQuestionLayout
              :ref="'tableQuestion' + instruction._id"
              :rows="instruction.tableRow"
              :cols="instruction.tableColumn"
              :data="instruction.tableData"
              :isCSV="instruction.type === 'importcsv'"
              displayType="inspector"
              :readOnly="isTaskCompleted"
              @onCellLeave="
                (row, col, val) => onCellLeave(instruction, row, col, val)
              "
              @onCellInput="
                (row, col, val) => onCellInput(instruction, row, col, val)
              "
            />
          </div>

          <div
            v-if="instruction.type === 'smarttablequestion'"
            class="question-details"
          >
            <div
              v-if="instruction.type === 'tablequestion'"
              class="table-description"
            >
              Table {{ instruction.dimensions }}
            </div>

            <div
              v-if="instruction.type === 'smarttablequestion'"
              class="image-container"
            >
              <div v-if="getTableImage(instruction)">
                <img :src="getTableImage(instruction)" alt="Table image" />
              </div>
              <div v-else>
                <b-icon icon="image-area" size="is-medium"></b-icon>
              </div>
            </div>
            <SmartTableQuestionLayout
              :ref="'tableQuestion' + instruction._id"
              :rows="instruction.tableRow"
              :cols="instruction.tableColumn"
              :data="instruction.tableData"
              :isCSV="instruction.type === 'importcsv'"
              displayType="inspector"
              :readOnly="isTaskCompleted"
              @onCellLeave="
                (row, col, val) => onCellLeave(instruction, row, col, val)
              "
              @onCellInput="
                (row, col, val) => onCellInput(instruction, row, col, val)
              "
            />
          </div>
        </div>
        <div v-if="instruction.internalDocumentsData">
          <LinkedDocuments :documents="instruction.internalDocumentsData" />
        </div>
      </div>
    </div>
    <b-modal v-if="isAnnotatorDisplayed" :active="true">
      <image-annotator
        :image="imageToBeAnnotated"
        @cancel="hideAnnotator"
        @ok="processAnnotator"
        @okandnew="newAnnotator"
        :nextCamera="annotatorCameraNext"
      />
    </b-modal>
    <image-gallery
      v-if="isGalleryDisplayed"
      v-model="selectedQuestion.image"
      type="instruction"
      :readOnly="isTaskCompleted"
      @close="hideGallery"
      @onDeleteImage="debouncedUpdateQuestion(selectedQuestion)"
      @onEditInformation="debouncedUpdateQuestion(selectedQuestion)"
      :inspection="selectedQuestion"
    />
  </div>
</template>

<script>
import { v4 } from 'uuid';
import { getDocument } from 'pdfjs-dist';
import 'pdfjs-dist/webpack.mjs';
import { debounce } from 'lodash';
import MobileDetect from 'mobile-detect';
import {
  CREATE_QUESTIONS,
  UPDATE_QUESTION,
} from '../../../store/questions/actions/actionTypes';
import Camera from '../../layout/Camera';
import ImageAnnotator from '../../layout/ImageAnnotator';
import ImageGallery from '../../layout/ImageGallery';
import { GET_CURRENT_USER } from '../../../store/users/getters/getterTypes';
import { GET_TASK_QUESTIONS } from '../../../store/questions/getters/getterTypes';
import { GET_CURRENT_JOB } from '../../../store/jobs/getters/getterTypes';
import { GET_JOB_ATTACHMENTS_COUNT } from '../../../store/jobs/actions/actionTypes';
import TableQuestionLayout from './components/TableQuestionLayout';
import SmartTableQuestionLayout from './components/SmartTableQuestionLayout';
import DIJVueConfigurations from '../../../helpers/DIJVueConfigurations';
import imageHelper from '../../../helpers/ImageHelper';
import onlineService from '../../../services/onlineService';
import ConflictDialog from '../../ConflictDialog';
import OfflineMixin from '../../../mixins/OfflineMixin';
import RichTextEditor from '../../RichTextEditor';
import LinkedDocuments from '../HyperlinkDocuments/LinkedDocuments';

const uuidv4 = v4;

export default {
  components: {
    Camera,
    TableQuestionLayout,
    SmartTableQuestionLayout,
    ImageAnnotator,
    ImageGallery,
    ConflictDialog,
    RichTextEditor,
    LinkedDocuments,
  },
  mixins: [OfflineMixin],
  data() {
    return {
      savingStates: {},
      uploadedFiles: 0,
      totalFilesToBeUploaded: 0,
      multipleImageUploaderToast: null,
      isItemSelectionDialogActive: false,
      selectedQuestion: null,
      tableData: [],
      isAnnotatorDisplayed: false,
      imageToBeAnnotated: null,
      isGalleryDisplayed: false,
      isCameraDisplayed: false,
      options: [
        {
          value: 'no',
          label: 'No',
          icon: 'close',
          type: 'is-danger is-light is-outlined',
        },
        {
          value: 'yes',
          label: 'Yes',
          icon: 'check',
          type: 'is-success is-light is-outlined',
        },
      ],
      imagesToUpload: [],
      debouncedUpdateQuestionFunctions: {},
      isTablet: false,
      annotatorCameraNext: false,
      isLoading: false,
      conflictDialogVisible: false,
      hasUnsavedChanges: false,
    };
  },
  props: {
    task: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    job() {
      return this.$store.getters[GET_CURRENT_JOB];
    },
    iconRequirePictureStyle() {
      return (instruction) => {
        if (this.isTaskCompleted) {
          return { color: '#9aa0a6' };
        }

        if (instruction.isPictureMandatory && instruction.image.length === 0) {
          return { color: 'red' };
        }

        return { color: '' };
      };
    },
    taskQuestions() {
      return this.$store.getters[GET_TASK_QUESTIONS];
    },
    user() {
      return this.$store.getters[GET_CURRENT_USER];
    },
    isTaskCompleted() {
      if (this.readOnly) {
        return true;
      }

      if (this.task.workStatusCode === '2') {
        return true;
      }

      if (this.job.jobCompleted) {
        return true;
      }

      return this.task.workStatusCode
        ? this.task.workStatusCode.toString() === '2' && this.job.jobCompleted
        : false;
    },
  },
  mounted() {
    const md = new MobileDetect(navigator.userAgent);
    this.isTablet = md.tablet() !== null;

    window.addEventListener('beforeunload', this.onBeforeUnload, {
      capture: true,
    });
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.onBeforeUnload, {
      capture: true,
    });
  },
  watch: {
    taskQuestions: {
      handler(newVal) {
        if (this.selectedQuestion && this.selectedQuestion._id) {
          const updated = newVal.find(
            (q) => q._id === this.selectedQuestion._id
          );
          if (updated) {
            this.selectedQuestion = { ...updated };
          }
        }
      },
      deep: true,
    },
  },

  methods: {
    onCheckOtherOption(instruction) {
      const otherIndex = instruction.multipleChoices.findIndex(
        (opt) => opt.other
      );
      if (otherIndex !== -1) {
        instruction.multipleChoices[otherIndex].checked =
          !instruction.multipleChoices[otherIndex].checked;
      }

      this.debouncedUpdateQuestion(instruction);
    },
    hasOtherOption(instruction) {
      return (
        instruction.multipleChoices.find((opt) => opt.other)?.checked || false
      );
    },
    inputOtherOption(instruction, event) {
      const otherOption = instruction.multipleChoices.find((opt) => opt.other);
      if (otherOption) {
        otherOption.defaultMC = event;
        this.debouncedUpdateQuestion(instruction);
      }
    },
    radioOptionChanged(instruction, optionIndex) {
      instruction.multipleChoices.forEach((opt, idx) => {
        opt.checked = idx === optionIndex;
      });
      this.debouncedUpdateQuestion(instruction);
    },
    otherSingleChoiceOption(instruction) {
      if (!instruction.multipleChoices.some((opt) => opt.other)) {
        instruction.multipleChoices.push({
          other: true,
          defaultMC: '',
          checked: true,
        });
      }
      const otherIndex = instruction.multipleChoices.findIndex(
        (opt) => opt.other
      );
      if (otherIndex !== -1) {
        for (let i = 0; i < instruction.multipleChoices.length; i++) {
          if (i !== otherIndex) {
            instruction.multipleChoices[i].checked = false;
          } else {
            instruction.multipleChoices[i].checked = true;
          }
        }
      }

      this.debouncedUpdateQuestion(instruction);
    },
    markDirty() {
      this.hasUnsavedChanges = true;
    },
    markClean() {
      this.hasUnsavedChanges = false;
    },
    onBeforeUnload(e) {
      if (!this.hasUnsavedChanges) return;
      e.preventDefault();
      e.returnValue = '';
    },
    onResolved(resolvedEntities) {
      resolvedEntities.forEach((resolvedEntity) => {
        this.$store.dispatch(UPDATE_QUESTION, resolvedEntity);
      });

      this.onCancel();
    },
    onCancel() {
      this.conflictDialogVisible = false;
    },
    isYesNoType(instruction) {
      return instruction.type === 'yesno';
    },
    onCellLeave(instruction, rowIndex, colIndex, newValue) {
      if (['tablequestion', 'importcsv'].includes(instruction.type)) {
        const currentData =
          this.$refs[`tableQuestion${instruction._id}`][0].getTempTableData();

        if (currentData[rowIndex][colIndex] !== newValue) {
          currentData[rowIndex][colIndex] = newValue;
          instruction.tableData = currentData;
          this.debouncedUpdateQuestion(instruction);
        }
      }
    },
    onCellInput(instruction, row, col, value) {
      this.updateTempCell(instruction, row, col, value);
      this.markDirty();

      instruction.tableData =
        this.$refs[`tableQuestion${instruction._id}`][0].getTempTableData();
      this.debouncedUpdateQuestion(instruction);
    },

    updateTempCell(instruction, row, col, value) {
      const temp =
        this.$refs[`tableQuestion${instruction._id}`][0].getTempTableData();
      if (temp[row][col] !== value) temp[row][col] = value;
    },

    showGallery(question) {
      if (this.isTaskCompleted) return;
      if (question.image && question.image.length > 0) {
        this.selectedQuestion = question;
        this.isGalleryDisplayed = true;
      }
    },
    hideGallery() {
      this.isGalleryDisplayed = false;
    },
    showAnnotator() {
      this.isAnnotatorDisplayed = true;
    },
    hideAnnotator() {
      this.isAnnotatorDisplayed = false;
      this.imageToBeAnnotated = null;
    },
    async onUploadImageWrapper(event, instruction, index) {
      if (this.annotatorCameraNext) {
        this.annotatorCameraNext = false;
      }
      await this.onUploadImage(event, instruction);

      this.$refs[`fileUploadDropdown${index}`][0].toggle();
    },
    async onUploadImage(_, instruction, fileNew) {
      const images = this.imagesToUpload;
      this.selectedQuestion = instruction;
      if (fileNew) {
        images.push(fileNew);
      }

      if (images.length === 0) {
        return;
      }

      if (images.length === 1 && images[0].type !== 'application/pdf') {
        const image = images[0];

        const fileReader = new FileReader();
        fileReader.onload = () => {
          const imageAnnotated = new Image();
          imageAnnotated.onload = () => {
            this.imageToBeAnnotated = imageAnnotated;
            this.showAnnotator();
          };
          imageAnnotated.src = fileReader.result;
        };
        fileReader.readAsDataURL(image);
      } else {
        const result = await this.$store.dispatch(
          GET_JOB_ATTACHMENTS_COUNT,
          this.job._id
        );
        const files = images;
        const pendingImagePushes = [];

        if (result.count + files.length > result.max) {
          this.$buefy.toast.open({
            duration: 5000,
            message: `Too many attachments. Maximum allowed is ${result.max} attachments.`,
            type: 'is-danger',
            position: 'is-top',
          });
          return;
        }

        this.isLoading = true;
        this.multipleImageUploaderToast = this.$buefy.toast.open({
          duration: 50000,
          message: 'Preparing files to be uploaded',
          type: 'is-warning',
          position: 'is-bottom',
        });

        this.uploadedFiles = 0;
        this.totalFilesToBeUploaded = files.length;
        this.multipleImageUploaderToast.message = `Uploading images ${this.uploadedFiles} / ${this.totalFilesToBeUploaded}`;

        for (let i = 0; i < files.length; i++) {
          const file = files[i];

          if (file.type === 'application/pdf') {
            // eslint-disable-next-line no-await-in-loop
            const dataURL = await this.handlePDF(file);
            const byteString = atob(dataURL.split(',')[1]);
            const mimeString = dataURL
              .split(',')[0]
              .split(':')[1]
              .split(';')[0];
            const byteNumbers = new Array(byteString.length);

            for (let j = 0; j < byteString.length; j++) {
              byteNumbers[j] = byteString.charCodeAt(j);
            }

            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: mimeString });
            const fileJsonName = `${uuidv4()}_pdf.png`;
            const fileJson = new File([blob], fileJsonName);

            const site = this.user.location;
            const path = `uploads/sites/${site}/${new Date().getFullYear()}/${
              this.task._id
            }`;
            // eslint-disable-next-line no-await-in-loop
            const url = await this.uploadToS3(
              fileJson,
              path,
              this.selectedQuestion._id
            );
            if (!url) return;
            pendingImagePushes.push(
              this.pushImage({
                description: '',
                name: '',
                path: url,
              })
            );
          } else {
            const site = this.user.location;
            const path = `uploads/sites/${site}/${new Date().getFullYear()}/${
              this.task._id
            }`;

            // eslint-disable-next-line no-await-in-loop
            const url = await this.uploadToS3(
              file,
              path,
              this.selectedQuestion._id
            );
            if (!url) return;
            pendingImagePushes.push(
              this.pushImage({
                description: '',
                name: '',
                path: url,
              })
            );
          }

          this.uploadedFiles++;
          this.multipleImageUploaderToast.message = `Uploading images ${this.uploadedFiles} / ${this.totalFilesToBeUploaded}`;

          if (this.uploadedFiles === this.totalFilesToBeUploaded) {
            setTimeout(() => {
              if (this.multipleImageUploaderToast) {
                this.multipleImageUploaderToast.close();
                this.multipleImageUploaderToast = null;
              }
            }, 2000);
          }
        }
        await Promise.all(pendingImagePushes);
        this.markDirty();
        this.debouncedUpdateQuestion(this.selectedQuestion);
      }
      this.imagesToUpload = [];
    },
    dataURLtoFile(dataurl, filename) {
      const arr = dataurl.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, {
        type: mime,
      });
    },
    async newAnnotator({ image, file, imageDesc }) {
      await this.processAnnotator({
        image,
        imageDesc,
      });
      if (this.annotatorCameraNext) {
        this.showCamera(this.selectedQuestion);
      } else {
        await this.onUploadImage(null, this.selectedQuestion, file);
      }
    },
    async processAnnotator({ image, imageDesc }) {
      this.isLoading = true;
      this.hideAnnotator();
      this.imageOriginalName = '';

      const fileName = `${uuidv4()}.jpg`;
      const canvas = document.createElement('canvas');
      imageHelper.drawImageFor1920px(canvas, image);
      const file = this.dataURLtoFile(
        canvas.toDataURL('image/jpeg', 0.9),
        fileName
      );

      const site = this.user.location;
      const path = `uploads/sites/${site}/${new Date().getFullYear()}/${
        this.task._id
      }`;

      const uploadUrl = await this.uploadToS3(
        file,
        path,
        this.selectedQuestion._id
      );

      if (!uploadUrl) return;

      this.$buefy.snackbar.open({
        duration: 3000,
        message: this.$t('picture_uploaded_successfully', {
          // image: this.imageOriginalName.toUpperCase(),
        }),
        type: 'is-success',
        position: 'is-bottom-left',
        queue: false,
      });

      await this.pushImage({
        description: imageDesc.description,
        name: imageDesc.name,
        path: uploadUrl,
      });

      this.debouncedUpdateQuestion(this.selectedQuestion);
    },

    pushImage(imageObj) {
      return new Promise((resolve) => {
        // Make Sure the image have time to upload befour update the UI
        setTimeout(() => {
          if (!this.selectedQuestion.image) {
            this.selectedQuestion.image = [];
          }
          this.selectedQuestion.image.push(imageObj);
          resolve();
        }, 1250);
      });
    },

    async uploadToS3(file, path, questionId) {
      try {
        const dijConfigurations = new DIJVueConfigurations();
        if (await onlineService.getStatus()) {
          const url = await dijConfigurations.uploadFileToS3(
            file,
            file.name,
            path
          );
          return url;
        }

        const url = await dijConfigurations.offlineUploadFileToS3(
          file,
          file.name,
          path,
          'question',
          questionId
        );

        return url;
      } catch (error) {
        this.isLoading = false;
        console.error('failed to load image', error);
      }
    },

    async onSaveTable(question) {
      if (this.savingStates[question._id]) return;

      const tableRef = this.$refs[`tableQuestion${question._id}`]?.[0];
      if (tableRef) {
        this.$refs[`tableQuestion${question._id}`][0].resetFocusedCell();
      }

      this.$set(this.savingStates, question._id, true);
      try {
        if (['tablequestion', 'importcsv'].includes(question.type)) {
          question.tableData =
            this.$refs[`tableQuestion${question._id}`][0].getTempTableData();
        }
        await this.updateQuestion(question);
      } finally {
        this.$set(this.savingStates, question._id, false);
      }
    },

    // Very ugly workaround untill we move the logic to use pdf on backend
    async handlePDF(file) {
      const pdf = await getDocument(URL.createObjectURL(file)).promise;
      const page = await pdf.getPage(1);
      const scale = 1;
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const renderContext = {
        canvasContext: context,
        viewport,
      };
      await page.render(renderContext).promise;
      return canvas.toDataURL();
    },
    async forceSave() {
      const questions = [];
      this.taskQuestions.forEach((question) => {
        if (['tablequestion', 'importcsv'].includes(question.type)) {
          question.tableData =
            this.$refs[`tableQuestion${question._id}`][0].getTempTableData();
          questions.push(question);
        } else {
          questions.push(question);
        }
      });

      await this.$store.dispatch(CREATE_QUESTIONS, {
        taskId: this.task._id,
        questions,
      });
    },
    getTableImage(instruction) {
      // For local env we need to point to the local s3
      if (instruction.tableImg && window.location.host.includes('localhost')) {
        return `http://localhost:4566/images${instruction.tableImg}`;
      }

      return instruction.tableImg || 'invalid-image';
    },
    getImageReference(instruction) {
      // For local env we need to point to the local s3
      if (
        instruction.imageReference &&
        window.location.host.includes('localhost')
      ) {
        return `http://localhost:4566/images${instruction.imageReference}`;
      }
      return instruction.imageReference || 'invalid-image';
    },
    debouncedUpdateQuestion(question) {
      if (!this.debouncedUpdateQuestionFunctions[question._id]) {
        this.debouncedUpdateQuestionFunctions[question._id] = debounce(
          this.updateQuestion,
          2000
        );
      }

      this.debouncedUpdateQuestionFunctions[question._id](question);
    },
    async updateQuestion(question) {
      await this.$store.dispatch(UPDATE_QUESTION, {
        ...question,
        isInspector: true,
      });

      this.markClean();

      // Let also the request to settle before we hide the loading spinner
      setTimeout(() => {
        this.isLoading = false;
      }, 200);
    },
    showCamera(instruction) {
      this.annotatorCameraNext = true;
      this.selectedQuestion = instruction;
      this.isCameraDisplayed = true;
    },
    hideCamera() {
      this.isCameraDisplayed = false;
    },
    processCamera(image) {
      this.hideCamera();
      this.imageToBeAnnotated = image;
      this.showAnnotator();
      this.filename = `${uuidv4()}.jpg`;
    },
  },
};
</script>

<style lang="scss" scoped>
.create-new {
  margin-bottom: 20px;
  min-height: 75px;
  font-size: larger;
  font-weight: bold;
  background: #f5f5f5;
  padding: 10px;
  border-radius: 10px;
}

.other-option {
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;

  .control {
    width: 100%;
  }
}

button span {
  width: 100%;
}

.full-width {
  width: 100%;
}

.subtitle {
  display: flex;
  flex-direction: row;
}

.table-description {
  align-items: start;
  display: flex;
  width: 100%;
  font-size: larger;
}

.question-details {
  font-size: medium;
  color: gray;
  display: flex;
  flex-direction: column;
  // align-items: start;
  width: 100%;
}

.question-details-yes-no {
  display: flex;
  flex-direction: row;
  gap: 10px;
}

.right-side-title {
  display: flex;
  flex-direction: row;
  margin-left: auto;
  align-items: center;
}

.req-picture {
  color: gray;
  font-style: italic;
  font-size: larger;
  white-space: nowrap; /* Keeps text on a single line */
}

.animate-slide {
  animation: slide-in-out 1s linear infinite;
}

@keyframes slide-in-out {
  0%,
  100% {
    transform: translateY(0%);
    opacity: 1;
  }
  50% {
    transform: translateY(
      7.5%
    ); /* Adjust this value for greater or less movement */
    opacity: 0.2;
  }
}

.instruction-action {
  color: gray;
  font-size: medium;
}

.instruction-action.is-disabled {
  color: #9aa0a6;
  opacity: 0.65;
  cursor: not-allowed;
  pointer-events: none;
}

.instruction-action:hover {
  cursor: pointer;
}

.items-container {
  gap: 10px;
  display: flex;
  flex-direction: column;
  overflow: scroll;
  overflow-x: hidden;
  padding: 0 10px;
  height: 100%;
}

.instruction {
  font-size: larger;
  font-weight: bold;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  justify-content: start;
  align-items: start;
  border-radius: 10px;
}

.instruction:hover {
  background: #f5f5f5a6;
}

.instruction-title {
  display: flex;
  flex-direction: row;
  width: 100%;
  background-color: white;
  padding: 5px;
  border-radius: 10px;
  font-size: x-large;

  span {
    white-space: pre-wrap;
    text-align: left;
  }
}

.instruction-details {
  display: flex;
  flex-direction: row;
  width: 100%;
  padding: 10px;
}

.justify-start {
  justify-content: start;
}

.wrapper {
  padding: 20px 10px;
  background-color: #dddddd;
  text-align: center;
  height: calc(100vh - 100px);
}

.btn-center-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding-right: 25px;
}

.btn-center-content-column {
  position: absolute;
  top: 10%;
  padding-right: 30px;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: center;
  gap: 5px;
}

.option {
  margin-right: 10px; /* Adjust spacing between options as needed */
  font-size: medium;
  font-weight: 600;
  color: gray;
}

.description {
  font-size: medium;
  font-weight: 600;
  color: gray;
}

.image-container {
  margin-bottom: 10px;
  width: 100%;
}

.image-container-helper {
  margin-top: 10px;
  margin-bottom: 10px;
  width: 100%;
  img {
    max-height: 250px;
  }
}

.icon-container {
  position: relative;
  display: inline-block;
  margin-top: 2.5px;
}

.image-counter {
  position: absolute;
  top: -5px; /* Adjust based on your icon's size */
  left: -5px; /* Adjust based on your icon's size */
  background-color: #23d160;
  color: white;
  border-radius: 50%;
  padding: 2px 5px;
  font-size: 0.75rem;
  min-width: 20px; /* Ensure it's round for single digits */
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dij-diagrambuilder-camera-modal ::v-deep div.animation-content.modal-content {
  overflow: hidden;
}

// .upload.control.file-label {
//   max-width: 45px;
// }

.child-question {
  margin-left: 30px;
}

.dropdown-item {
  display: flex;
  align-items: center;
  flex-direction: row;
  width: 300px;

  span {
    display: flex;
    align-items: center;
    color: #808080;
  }
}
.dropdown-content {
  a {
    display: flex;
    max-width: 192px;
  }
}
.upload.control.file-label {
  align-items: center;
  font-size: 0.875rem;
  color: #808080;
}
</style>

<style lang="scss"></style>
